import { useReactiveVar } from '@apollo/client';
import { nextI18nextConfig } from '@goed-platform/shared/constants';
import { isLoggedInVar, userInfoVar } from '@goed-platform/shared/data-access';
import { CustomAppProps } from '@goed-platform/shared/types';
import { BaseLayout, SEOTags } from '@goed-platform/shared/ui';
import { GetServerSideProps } from 'next';
import { UserConfig, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { FC, useEffect } from 'react';

type PageProps = CustomAppProps;

type CheckFieldProps = {
    key: string;
    value: unknown;
};

const useScript = (url) => {
    useEffect(() => {
        const script = document.createElement('script');

        script.src = url;
        script.async = true;

        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, [url]);
};

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
    return {
        props: {
            ...(await serverSideTranslations(locale, ['common'], nextI18nextConfig as UserConfig)),
        },
    };
};

const Appoint: FC<PageProps> = ({ navigation }: PageProps): JSX.Element => {
    const router = useRouter();
    const { t } = useTranslation();
    const userInfo = useReactiveVar(userInfoVar);
    const widgetId = router.query.widgetId;

    useScript(`${process.env.NEXT_PUBLIC_APPOINT_URL}/nl/public/js/widget.min.js`);

    const fields = {
        name: userInfo?.lastName,
        first_name: userInfo?.firstName,
        street: userInfo?.billingAddress?.streetName,
        number: userInfo?.billingAddress?.houseNumber,
        bus: userInfo?.billingAddress?.box,
        postalcode: userInfo?.billingAddress?.postCode,
        city: userInfo?.billingAddress?.city,
        country_iso_code: 'BEL',
        country: 'België',
        mobile: userInfo?.billingAddress?.phone,
        email: userInfo?.billingAddress?.email,
        date_of_birth: userInfo?.dateOfBirth,
        lng: userInfo?.language,
        sex: userInfo?.sex,
        nationality: 'Belgian',
    };

    const checkField = ({ key, value }: CheckFieldProps): string => {
        if (value && (typeof value !== 'string' || value.length > 0)) {
            return `&field[${key}]=${value}`;
        }

        return '';
    };

    const constructIframeUrl = (): string => {
        let url = `/e/${router.query.key}?`;

        if (router.query.type) {
            url += `type=${router.query.type}`;
        }

        if (router.query.typegroup) {
            url += `typegroup=${router.query.typegroup}`;
        }

        if (router.query.reference) {
            url += `reference=${router.query.reference}`;
        }

        if (router.query.vreference) {
            url += `vreference=${router.query.vreference}`;
        }

        if (isLoggedInVar() && userInfoVar()) {
            for (const key in fields) {
                url += checkField({ key, value: fields[key] });
            }
        }

        return url;
    };

    return (
        <BaseLayout navigation={navigation}>
            <>
                <SEOTags
                    pageTitle={t('appoint.detail.meta.title')}
                    metaDescription={t('appoint.detail.meta.description')}
                />
                <div className="container">
                    <div className="row">
                        <div className="col-12 mb-6">
                            <div
                                className={`appoint-widget appoint-widget-${widgetId}`}
                                data-token-nl={constructIframeUrl()}
                            ></div>
                        </div>
                    </div>
                </div>
            </>
        </BaseLayout>
    );
};

export default Appoint;
